import React, { useMemo } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Heart, MessageCircle } from 'lucide-react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { PostComments } from './PostComments';
import { PostImages } from './post/PostImages';
import { usePostActions } from '@/hooks/usePostActions';
import { usePostComments } from '@/hooks/usePostComments';
import { PostDetailDialogProps } from '@/types/community-props.types';
import { UserProfileHoverCard } from './UserProfileHoverCard';

export function PostDetailDialog({ 
  post, 
  open, 
  onOpenChange, 
  onLikeChange 
}: PostDetailDialogProps) {
  const { isLiked, likesCount, toggleLike, isLoading } = usePostActions({
    postId: post.id,
    initialLiked: post.is_liked || false,
    initialLikes: post.likes_count,
    onLikeChange
  });

  const {
    comments,
    isLoading: isLoadingComments,
    newComment,
    setNewComment,
    handleAddComment,
    isAddingComment,
    replyingTo,
    setReplyingTo,
    replyContent,
    setReplyContent,
    handleAddReply,
    isAddingReply
  } = usePostComments(post.id);

  // Calcular contagem real de comentários (principais + replies)
  const actualCommentsCount = useMemo(() => {
    return comments.reduce((total, comment) => {
      return total + 1 + (comment.replies?.length || 0);
    }, 0);
  }, [comments]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[calc(100vw-2rem)] sm:max-w-3xl max-h-[90vh] overflow-y-auto p-0">
        {/* Header */}
        <div className="sticky top-0 bg-background border-b p-4 sm:p-6 z-10">
          <div className="flex items-center gap-3">
            <Avatar className="h-10 w-10 sm:h-12 sm:w-12">
              <AvatarImage src={post.author?.logo_url || ''} />
              <AvatarFallback>
                {post.author?.full_name?.charAt(0).toUpperCase() || 'U'}
              </AvatarFallback>
            </Avatar>
            <div>
              <UserProfileHoverCard userId={post.author?.id ?? ''}>
                <p className="font-semibold text-gray-900 hover:text-primary cursor-pointer transition-colors">
                  {post.author?.full_name || 'Usuário removido'}
                </p>
              </UserProfileHoverCard>
              <p className="text-sm text-gray-500">
                {formatDistanceToNow(new Date(post.created_at), {
                  addSuffix: true,
                  locale: ptBR
                })}
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
          {/* Post Content */}
          <div>
            <p className="text-gray-900 whitespace-pre-wrap text-base leading-relaxed">
              {post.content}
            </p>
          </div>

          {/* Post Images */}
          {post.image_urls && post.image_urls.length > 0 && (
            <PostImages imageUrls={post.image_urls} />
          )}

          {/* Post GIF */}
          {post.gif_url && (
            <img src={post.gif_url} alt="GIF" className="rounded-lg max-h-64 w-auto" loading="lazy" />
          )}

          {/* Actions */}
          <div className="flex items-center gap-4 pt-2 border-t">
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleLike}
              disabled={isLoading}
              className={`flex items-center gap-2 ${
                isLiked ? 'text-red-500' : 'text-gray-500'
              }`}
            >
              <Heart 
                className={`h-4 w-4 ${isLiked ? 'fill-current' : ''}`} 
              />
              <span>{likesCount}</span>
            </Button>

            <div className="flex items-center gap-2 text-gray-500">
              <MessageCircle className="h-4 w-4" />
              <span>{actualCommentsCount}</span>
            </div>
          </div>

          {/* Comments Section */}
          <div className="border-t pt-6">
            <h3 className="font-semibold text-gray-900 mb-4">Comentários</h3>
            <PostComments
              comments={comments}
              isLoading={isLoadingComments}
              newComment={newComment}
              onCommentChange={setNewComment}
              onSubmitComment={handleAddComment}
              isSubmitting={isAddingComment}
              replyingTo={replyingTo}
              setReplyingTo={setReplyingTo}
              replyContent={replyContent}
              setReplyContent={setReplyContent}
              onSubmitReply={handleAddReply}
              isAddingReply={isAddingReply}
              postId={post.id}
            />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
